import { ContextFilter, ContextFilterItem } from "@reporting/rp-types/analytics";

export const mergeDynamicPeriodFilters = (filters: ContextFilter[]): ContextFilter[] => {
	// The graph unions all items of a single filter (p_PERIOD_DYNAMIC gets the whole list),
	// but extra entries with the same dim are intersected and only the first one reaches
	// the axis — one multi-item 'PX' filter is the only shape the server evaluates
	// correctly. Entries with empty items are kept separate (unfinished UI drafts).
	const dynamic = filters?.filter(f => f.dim == 'PX' && (typeof f.items === 'string' || f.items?.length)) ?? [];
	if (dynamic.length < 2) return filters;
	const items = dynamic.flatMap(f =>
		(typeof f.items === 'string' ? [f.items] : f.items) as (ContextFilterItem|string)[]
	) as ContextFilterItem[] | string[];
	return filters
		.filter(f => !dynamic.includes(f) || f == dynamic[0])
		.map(f => f == dynamic[0] ? {...f, items} : f);
}

/** Concrete periods: a filter on any of them replaces a filter on another one (a common PM filter replaces an item's PW). */
const CONCRETE_PERIODS = ['PM', 'PW', 'PX'];

/** Legacy "global filters off" lock (ReportContextDataConvertor): dimensions 1-9 stand for all numeric dimensions. */
const LEGACY_LOCK_ALL = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

/**
 * The key a filter is matched by when the common filters of a dashboard meet the filters of an
 * item: the concrete periods (PM, PW, PX) share one key, the common period (PC) and every other
 * dimension have their own - a numeric dimension its number, its attribute filters ('4:…') included.
 */
const filterKey = (dim?: string) => {
	const key = (dim ?? '').split(':')[0].toUpperCase();
	return CONCRETE_PERIODS.includes(key) ? 'P' : key;
}

/**
 * Filters of a dashboard item with the common filters of the dashboard applied: a common filter
 * replaces the item's filters with the same key (filterKey), the item keeps the rest. Its locked
 * dimensions (MultiContextDataItem.locked_dimensions) take no common filter: 'P' any period
 * (PC included), otherwise the dimension code.
 */
export const mergeCommonFilters = (common: ContextFilter[], item: ContextFilter[], lockedDimensions: string[] = []): ContextFilter[] => {
	const locked = new Set((lockedDimensions ?? []).map(d => d.toString().toUpperCase()));
	const lockAllNumeric = LEGACY_LOCK_ALL.every(d => locked.has(d));
	const isLocked = (dim?: string) => {
		const key = filterKey(dim);
		return locked.has(key)
			|| (locked.has('P') && (dim ?? '').charAt(0).toUpperCase() == 'P')
			|| (lockAllNumeric && /^\d+$/.test(key));
	};
	const applied = (common ?? []).filter(f => !isLocked(f.dim));
	const overridden = new Set(applied.map(f => filterKey(f.dim)));
	return [...applied, ...(item ?? []).filter(f => !overridden.has(filterKey(f.dim)))];
}
